import { useCallback, useEffect, useRef, useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { python } from "@codemirror/lang-python";
import { sql } from "@codemirror/lang-sql";
import { EditorView } from "@codemirror/view";
import {
  FileCodeIcon,
  XIcon,
  Loader2Icon,
  LockIcon,
  AlertCircleIcon,
} from "lucide-react";
import { Modal, Button, Group, Text } from "@mantine/core";

import Message from "../../../../../i18n/Message";
import label from "../../../../../i18n/label";
import ImagePreview from "./ImagePreview";
import BinaryFileMessage from "./BinaryFileMessage";

import "./FileViewer.scss";

// Get language extension based on file extension
const getLanguageExtension = (extension) => {
  const ext = (extension || "").toLowerCase();

  // JavaScript/TypeScript
  if (["js", "jsx", "ts", "tsx", "mjs", "cjs"].includes(ext)) {
    return javascript({ jsx: ["jsx", "tsx"].includes(ext), typescript: ["ts", "tsx"].includes(ext) });
  }

  // HTML/XML
  if (["html", "htm", "xml", "svg"].includes(ext)) {
    return html();
  }

  // CSS/SCSS
  if (["css", "scss", "sass", "less"].includes(ext)) {
    return css();
  }

  // JSON
  if (ext === "json") {
    return json();
  }

  // Markdown
  if (["md", "mdx", "markdown"].includes(ext)) {
    return markdown();
  }

  // Python
  if (["py", "pyw", "pyx"].includes(ext)) {
    return python();
  }

  // SQL
  if (ext === "sql") {
    return sql();
  }

  // YAML - treat as plain text
  if (["yaml", "yml"].includes(ext)) {
    return [];
  }

  // Default: no syntax highlighting
  return [];
};

// Check if file is an image
const isImageFile = (extension) => {
  const ext = (extension || "").toLowerCase();
  return ["png", "jpg", "jpeg", "gif", "svg", "webp", "ico", "bmp"].includes(ext);
};

// Check if file is binary (non-text)
const isBinaryFile = (extension) => {
  const ext = (extension || "").toLowerCase();
  const binaryExtensions = [
    // Executables
    "exe", "dll", "so", "dylib",
    // Archives
    "zip", "tar", "gz", "rar", "7z",
    // Media
    "mp3", "mp4", "wav", "avi", "mov", "mkv",
    // Documents
    "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx",
    // Fonts
    "ttf", "otf", "woff", "woff2", "eot",
    // Other
    "bin", "dat",
  ];
  return binaryExtensions.includes(ext);
};

// Get file extension from path
const getExtension = (path) => {
  if (!path) return "";
  const parts = path.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
};

// Tab component
const Tab = ({ tab, isActive, onActivate, onClose }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    onActivate(tab.path);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    onClose(tab.path);
  };

  const isStale = tab.isStale || tab.pendingContent != null;

  return (
    <div
      className={`tab ${isActive ? "active" : ""} ${tab.isDirty ? "dirty" : ""} ${isStale ? "stale" : ""}`}
      onClick={handleClick}
    >
      {tab.isDirty && <span className="dirty_indicator">●</span>}
      {isStale && !tab.isDirty && (
        <span className="stale_indicator" title="File changed externally">
          <AlertCircleIcon size={12} />
        </span>
      )}
      <span className="tab_name">{tab.name}</span>
      {tab.isSaving ? (
        <span className="tab_saving">
          <Loader2Icon size={12} className="spinning" />
        </span>
      ) : (
        <button
          className="tab_close"
          onClick={handleClose}
          title="Close"
        >
          <XIcon size={12} />
        </button>
      )}
    </div>
  );
};

// Editor component with CodeMirror
const Editor = ({ tab, onChange, onSave, readOnly }) => {
  const saveTimeoutRef = useRef(null);
  const lastSavedContentRef = useRef(tab.lastSavedContent);

  // Update ref when lastSavedContent changes
  useEffect(() => {
    lastSavedContentRef.current = tab.lastSavedContent;
  }, [tab.lastSavedContent]);

  // Get language extension
  const extension = getExtension(tab.path);
  const languageExtension = useMemo(() => getLanguageExtension(extension), [extension]);

  // Handle content change with auto-save
  const handleChange = useCallback(
    (value) => {
      onChange(tab.path, value);

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Auto-save after 500ms if content changed from last saved
      if (!readOnly && value !== lastSavedContentRef.current) {
        saveTimeoutRef.current = setTimeout(() => {
          onSave(tab.path, value);
        }, 500);
      }
    },
    [tab.path, onChange, onSave, readOnly]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Editor extensions
  const extensions = useMemo(() => {
    const exts = [
      EditorView.lineWrapping, // We actually want no wrapping per requirements
    ];

    // Add language extension if available
    if (languageExtension && Array.isArray(languageExtension) && languageExtension.length === 0) {
      // No extension
    } else if (languageExtension) {
      exts.push(languageExtension);
    }

    return exts;
  }, [languageExtension]);

  // Remove line wrapping - use horizontal scroll
  const noWrapExtensions = useMemo(() => {
    const exts = [];

    if (languageExtension && Array.isArray(languageExtension) && languageExtension.length === 0) {
      // No extension
    } else if (languageExtension) {
      exts.push(languageExtension);
    }

    return exts;
  }, [languageExtension]);

  return (
    <div className="editor_container">
      {readOnly && (
        <div className="readonly_banner">
          <LockIcon size={14} />
          <span>
            <Message id="builder_file_readonly" defaultMessage="This file is read-only" />
          </span>
        </div>
      )}
      <CodeMirror
        value={tab.content || ""}
        height="100%"
        theme="light"
        extensions={noWrapExtensions}
        onChange={handleChange}
        readOnly={readOnly}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightSpecialChars: true,
          foldGutter: true,
          drawSelection: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          syntaxHighlighting: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
          rectangularSelection: true,
          crosshairCursor: false,
          highlightActiveLine: true,
          highlightSelectionMatches: true,
          closeBracketsKeymap: true,
          defaultKeymap: true,
          searchKeymap: true,
          historyKeymap: true,
          foldKeymap: true,
          completionKeymap: true,
          lintKeymap: true,
        }}
      />
    </div>
  );
};

// Check if file is read-only (Docker/nginx config files)
const isReadOnlyFile = (path) => {
  if (!path) return false;
  const filename = path.split("/").pop().toLowerCase();
  return [
    "dockerfile",
    "docker-compose.yml",
    "docker-compose.yaml",
    ".dockerignore",
    "nginx.conf",
  ].includes(filename);
};

// Conflict modal component
const FileConflictModal = ({ tab, intl, onKeepMine, onReload }) => {
  if (!tab) return null;

  const fileName = tab.path.split("/").pop();

  return (
    <Modal
      opened={true}
      onClose={() => {}}
      withCloseButton={false}
      centered
      size="sm"
    >
      <Text fw={500} size="lg" mb="xs">
        <Message id="builder_file_changed_externally" defaultMessage="File Changed Externally" />
      </Text>
      <Text size="sm" c="dimmed" mb="md">
        <Message
          id="builder_file_conflict_message"
          defaultMessage='"{fileName}" was modified by the agent, but you have unsaved changes.'
          values={{ fileName }}
        />
      </Text>

      <Group justify="flex-end" mt="md">
        <Button variant="outline" onClick={() => onKeepMine(tab.path)}>
          <Message id="builder_keep_my_changes" defaultMessage="Keep My Changes" />
        </Button>
        <Button color="blue" onClick={() => onReload(tab.path)}>
          <Message id="builder_discard_reload" defaultMessage="Discard & Reload" />
        </Button>
      </Group>
    </Modal>
  );
};

const FileViewer = ({
  tabs = [],
  activeTabPath,
  activeTab,
  onTabClose,
  onTabActivate,
  onContentChange,
  onSave,
  workspaceId,
  projectUid,
  intl,
  conflictTabs = [],
  onConflictKeepMine,
  onConflictReload,
}) => {
  // Find the first conflict tab to show in modal
  const conflictTab = conflictTabs.length > 0 ? conflictTabs[0] : null;

  // Empty state when no tabs open
  if (!tabs || tabs.length === 0) {
    return (
      <div className="file_viewer empty">
        <div className="placeholder">
          <FileCodeIcon size={48} color="#d1d5db" />
          <p className="placeholder_text">
            <Message
              id="builder_no_file_selected"
              defaultMessage="Select a file to view"
            />
          </p>
        </div>
      </div>
    );
  }

  // Get active tab content
  const currentTab = activeTab || tabs.find((t) => t.path === activeTabPath) || tabs[0];
  const extension = getExtension(currentTab?.path);
  const isImage = isImageFile(extension);
  const isBinary = isBinaryFile(extension);
  const isReadOnly = isReadOnlyFile(currentTab?.path);

  // Loading state for content
  const isLoading = currentTab && currentTab.content === null && !currentTab.loadError;

  return (
    <div className="file_viewer">
      {/* Conflict modal */}
      {conflictTab && (
        <FileConflictModal
          tab={conflictTab}
          intl={intl}
          onKeepMine={onConflictKeepMine}
          onReload={onConflictReload}
        />
      )}

      {/* Tab row */}
      <div className="tabs_row">
        <div className="file_tabs_container">
          {tabs.map((tab) => (
            <Tab
              key={tab.path}
              tab={tab}
              isActive={tab.path === activeTabPath}
              onActivate={onTabActivate}
              onClose={onTabClose}
            />
          ))}
        </div>
      </div>

      {/* Content area */}
      <div className="content_area">
        {isLoading ? (
          <div className="loading_state">
            <Loader2Icon size={24} className="spinning" />
            <p>
              <Message id="builder_loading_file" defaultMessage="Loading file..." />
            </p>
          </div>
        ) : currentTab?.loadError ? (
          <div className="error_state">
            <p>{currentTab.loadError}</p>
          </div>
        ) : isImage ? (
          <ImagePreview
            path={currentTab.path}
            workspaceId={workspaceId}
            projectUid={projectUid}
          />
        ) : isBinary ? (
          <BinaryFileMessage
            path={currentTab.path}
            workspaceId={workspaceId}
            projectUid={projectUid}
          />
        ) : (
          <Editor
            tab={currentTab}
            onChange={onContentChange}
            onSave={onSave}
            readOnly={isReadOnly}
          />
        )}
      </div>
    </div>
  );
};

export default FileViewer;
